import React from 'react';
import { ChevronLeft, ChevronRight, Eye } from 'lucide-react';
import type { NebulaConversationSummary } from '../../../../types/nebula-insights';
import { formatRelativeTime, formatUserName } from '../utils';

interface ConversationsTableProps {
  conversations: NebulaConversationSummary[];
  loading: boolean;
  page: number;
  totalPages: number;
  total: number;
  onPageChange: (page: number) => void;
  onOpenConversation: (conversationId: string) => void;
}

const ConversationsTable: React.FC<ConversationsTableProps> = ({
  conversations,
  loading,
  page,
  totalPages,
  total,
  onPageChange,
  onOpenConversation,
}) => (
  <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden relative">
    {loading && (
      <div className="absolute inset-0 bg-white/75 flex items-center justify-center z-10 backdrop-blur-sm">
        <div className="w-8 h-8 border-4 border-gray-300 border-t-gray-800 rounded-full animate-spin" />
      </div>
    )}

    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b border-gray-300">
            <th className="text-left py-3 px-4 text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide sidebar-menu-item">
              Conversation
            </th>
            <th className="text-left py-3 px-4 text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide sidebar-menu-item">
              Artist / User
            </th>
            <th className="text-left py-3 px-4 text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide sidebar-menu-item hidden md:table-cell">
              Messages
            </th>
            <th className="text-left py-3 px-4 text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide sidebar-menu-item hidden lg:table-cell">
              Model
            </th>
            <th className="text-left py-3 px-4 text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide sidebar-menu-item hidden sm:table-cell">
              Last active
            </th>
            <th className="w-12 py-3 px-4" />
          </tr>
        </thead>
        <tbody>
          {conversations.length === 0 ? (
            <tr>
              <td colSpan={6} className="py-12 text-center text-sm text-gray-500 font-family-messina-sans">
                No conversations found
              </td>
            </tr>
          ) : (
            conversations.map((conversation) => (
              <tr
                key={conversation.id}
                className="border-b border-gray-200 hover:bg-gray-50 transition-colors"
              >
                <td className="py-3 px-4">
                  <button
                    type="button"
                    onClick={() => onOpenConversation(conversation.id)}
                    className="text-left"
                  >
                    <p className="text-[0.875rem] text-gray-900 font-family-messina-sans font-medium line-clamp-1 hover:text-gray-700">
                      {conversation.title || 'Untitled conversation'}
                    </p>
                  </button>
                </td>
                <td className="py-3 px-4">
                  <p className="text-[0.875rem] text-gray-900 font-family-messina-sans">
                    {conversation.artistName || 'Unnamed artist'}
                  </p>
                  <p className="text-xs text-gray-500 font-family-messina-sans mt-0.5">
                    {formatUserName(
                      conversation.user?.firstname,
                      conversation.user?.lastname,
                      conversation.user?.email || '—'
                    )}
                  </p>
                </td>
                <td className="py-3 px-4 hidden md:table-cell text-[0.875rem] text-gray-900 font-family-messina-sans tabular-nums">
                  {conversation.messageCount}
                </td>
                <td className="py-3 px-4 hidden lg:table-cell">
                  {conversation.model ? (
                    <span className="inline-flex px-2 py-1 rounded bg-gray-100 text-xs text-gray-700 font-family-messina-sans">
                      {conversation.model}
                    </span>
                  ) : (
                    <span className="text-xs text-gray-400">—</span>
                  )}
                </td>
                <td className="py-3 px-4 hidden sm:table-cell text-xs text-gray-500 font-family-messina-sans">
                  {formatRelativeTime(conversation.lastActiveAt ?? conversation.updatedAt)}
                </td>
                <td className="py-3 px-4">
                  <button
                    type="button"
                    onClick={() => onOpenConversation(conversation.id)}
                    className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-800 transition-colors"
                    aria-label="View conversation"
                  >
                    <Eye size={16} />
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>

    <div className="px-4 py-3 border-t border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
      <p className="text-xs text-gray-500 font-family-messina-sans">
        Showing {conversations.length} of {total} conversation{total === 1 ? '' : 's'}
      </p>
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
          className="px-2.5 py-1.5 rounded-lg border border-gray-200 text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
        >
          <ChevronLeft size={16} />
        </button>
        <span className="text-xs text-gray-600 font-family-messina-sans tabular-nums">
          Page {page} of {Math.max(totalPages, 1)}
        </span>
        <button
          type="button"
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
          className="px-2.5 py-1.5 rounded-lg border border-gray-200 text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
        >
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  </div>
);

export default ConversationsTable;
